import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStore, faSpinner, faPencilAlt, faArchive, faPlus } from '@fortawesome/free-solid-svg-icons';
import { ClusterShop } from '../hooks/useClusterShops';
import { Button } from '../../../ui';
import ShopEditModal from './ShopEditModal';
import { useChains } from '../hooks/useChains';
import { url } from '../../../api';
import { apiClient } from '../../../api/client';
import { getAuthToken } from '../../../utils/authTokens';
import { toast } from 'react-toastify';
import { useConfirm } from '../../../ui/components/ConfirmDialog';
import { coffeeShopPaths } from '../../coffee_card_web/api/paths';

const authHeaders = () => ({ headers: { Authorization: `Bearer ${getAuthToken()}` } });

interface ClusterShopsListProps {
    clusterId: string | null;
    clusterName?: string;
    clusterCenter?: { lat: number; lng: number };
    clusterCity?: string;
    clusterCountry?: string;
    shops: ClusterShop[];
    loading: boolean;
    onRefetch: () => void;
    onAddShop?: () => void;
    editingShop?: ClusterShop | null;
    onEditShop?: (shop: ClusterShop | null) => void;
}

const statusBadgeClass = (status: string) => {
    switch (status) {
        case 'approved': return 'bg-green-100 text-green-800';
        case 'pending_review': return 'bg-amber-100 text-amber-800';
        case 'archived': return 'bg-gray-100 text-gray-600';
        case 'rejected': return 'bg-red-100 text-red-800';
        default: return 'bg-gray-100 text-gray-700';
    }
};

const ClusterShopsList: React.FC<ClusterShopsListProps> = ({
    clusterId,
    clusterName,
    clusterCenter,
    clusterCity,
    clusterCountry,
    shops,
    loading,
    onRefetch,
    onAddShop,
    editingShop,
    onEditShop
}) => {
    const confirm = useConfirm();
    const [internalEditing, setInternalEditing] = useState<ClusterShop | null>(null);
    const editing = editingShop !== undefined ? editingShop : internalEditing;
    const setEditing = onEditShop ?? setInternalEditing;
    const [archivingId, setArchivingId] = useState<string | null>(null);
    const { chains, refetch: refetchChains } = useChains();

    const handleSave = async (id: string, data: { shopType?: string; chainId?: string | null; name?: string; address?: string; city?: string; country?: string }) => {
        await apiClient.put(`${url}${coffeeShopPaths.byId(id)}`, data, authHeaders());
        toast.success('Shop updated');
        onRefetch();
    };

    const handleArchive = async (id: string) => {
        const ok = await confirm({
            title: "Archive shop?",
            message: "It will be hidden from public views.",
            confirmLabel: "Archive",
            tone: "danger",
        });
        if (!ok) return;
        setArchivingId(id);
        try {
            await apiClient.patch(`${url}${coffeeShopPaths.archive(id)}`, {}, authHeaders());
            toast.success('Shop archived');
            onRefetch();
        } catch {
            toast.error('Failed to archive');
        } finally {
            setArchivingId(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <FontAwesomeIcon icon={faSpinner} className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">{clusterName || 'Cluster'} shops</h3>
                {onAddShop && clusterId && (
                    <Button size="sm" onClick={onAddShop}>
                        <FontAwesomeIcon icon={faPlus} className="mr-1" />
                        Add shop
                    </Button>
                )}
            </div>

            {shops.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                    No shops in this cluster yet. Use &quot;Fetch from Google&quot; above to populate from Google Places, or add one manually.
                </div>
            ) : (
                <div className="space-y-2">
                    {shops.map((shop) => (
                        <div
                            key={shop._id}
                            className="p-3 border border-ink/15 rounded-lg bg-paper space-y-2"
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                    <div className="font-medium flex items-center gap-2">
                                        <FontAwesomeIcon icon={faStore} className="h-3 w-3 text-muted-foreground" />
                                        {shop.name}
                                    </div>
                                    {shop.address && (
                                        <div className="text-xs text-muted-foreground mt-0.5 truncate">{shop.address}</div>
                                    )}
                                    <div className="flex flex-wrap gap-2 mt-1">
                                        <span className={`text-xs px-2 py-0.5 rounded ${statusBadgeClass(shop.status)}`}>
                                            {shop.status.replace('_', ' ')}
                                        </span>
                                        <span className="text-xs text-muted-foreground">{shop.shopType.replace('-', ' ')}</span>
                                        {shop.chainName && (
                                            <span className="text-xs text-muted-foreground">· {shop.chainName}</span>
                                        )}
                                        {shop.totalRatings > 0 && (
                                            <span className="text-xs text-muted-foreground">
                                                · {shop.averageRating.toFixed(1)} ({shop.totalRatings} ratings)
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex gap-1 shrink-0">
                                    {shop.status !== 'archived' && (
                                        <>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setEditing(shop)}
                                            >
                                                <FontAwesomeIcon icon={faPencilAlt} className="h-3 w-3" />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleArchive(shop._id)}
                                                disabled={!!archivingId}
                                            >
                                                {archivingId === shop._id ? (
                                                    <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
                                                ) : (
                                                    <FontAwesomeIcon icon={faArchive} className="h-3 w-3" />
                                                )}
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ShopEditModal
                open={!!editing}
                onClose={() => setEditing(null)}
                shop={editing}
                chains={chains}
                onSave={handleSave}
            />
        </div>
    );
};

export default ClusterShopsList;
